import { useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useResponsive } from "@/hooks/use-responsive";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/shared/apiClient";
import {
  useCancelLeaveRequest,
  useCreateLeaveRequest,
  useUpdateLeaveRequest,
} from "../hooks";
import { TEMP_LEAVE_TYPES } from "../constants";
import type { LeaveRequestDisplayItem } from "@/features/shifts/types";

interface LeaveRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingRequest?: LeaveRequestDisplayItem | null;
}

interface LeaveFormState {
  leave_type_id: number;
  start_date: string;
  end_date: string;
  booking_unit: "full" | "half";
  reason: string;
  attachment_url: string;
}

function getApiErrorMessage(error: ApiError): string {
  const body = error.responseBody as
    | { message?: string; error?: { message?: string } }
    | undefined;
  return (
    body?.error?.message ??
    body?.message ??
    error.message ??
    "Failed to submit leave request."
  );
}

const DEFAULT_FORM: LeaveFormState = {
  leave_type_id: TEMP_LEAVE_TYPES[0].id,
  start_date: "",
  end_date: "",
  booking_unit: "full",
  reason: "",
  attachment_url: "",
};

function mapEditingRequestToForm(request?: LeaveRequestDisplayItem | null): LeaveFormState {
  if (!request) return DEFAULT_FORM;
  return {
    leave_type_id: request.leave_type_id,
    start_date: request.start_date,
    end_date: request.end_date,
    booking_unit: request.booking_unit,
    reason: request.reason ?? "",
    attachment_url: "",
  };
}

export function LeaveRequestModal({
  open,
  onOpenChange,
  editingRequest,
}: LeaveRequestModalProps) {
  const { isDesktop } = useResponsive();
  const { toast } = useToast();
  const isEditing = editingRequest != null;
  const createMutation = useCreateLeaveRequest();
  const updateMutation = useUpdateLeaveRequest();
  const cancelMutation = useCancelLeaveRequest();
  const [form, setForm] = useState<LeaveFormState>(DEFAULT_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isPending =
    createMutation.isPending || updateMutation.isPending || cancelMutation.isPending;

  useEffect(() => {
    if (!open) return;
    setForm(mapEditingRequestToForm(editingRequest));
    setSubmitError(null);
  }, [open, editingRequest]);

  const isSubmitDisabled = useMemo(() => {
    return !form.start_date || !form.end_date || !form.leave_type_id || isPending;
  }, [form.end_date, form.leave_type_id, form.start_date, isPending]);

  const handleSubmit = async () => {
    const payload = {
      leave_type_id: form.leave_type_id,
      start_date: form.start_date,
      end_date: form.end_date,
      booking_unit: form.booking_unit,
      reason: form.reason || undefined,
      attachment_url: form.attachment_url || undefined,
    };

    try {
      setSubmitError(null);
      if (isEditing && editingRequest) {
        await updateMutation.mutateAsync({ id: editingRequest.leave_request_id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiError) {
        console.error("Leave request API error", {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          responseBody: error.responseBody,
          payload,
        });
        const details = getApiErrorMessage(error);
        setSubmitError(details);
        toast({
          title: "Leave request failed",
          description: details,
          variant: "destructive",
        });
      } else {
        console.error("Leave request error", { error, payload });
        const details = error instanceof Error ? error.message : "Unknown error";
        setSubmitError(details);
        toast({
          title: "Leave request failed",
          description: details,
          variant: "destructive",
        });
      }
    }
  };

  const handleCancelRequest = async () => {
    if (!editingRequest) return;
    await cancelMutation.mutateAsync(editingRequest.leave_request_id);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={isDesktop ? "w-1/2 sm:max-w-none overflow-y-auto" : "w-full sm:max-w-none overflow-y-auto"}
      >
        <SheetHeader>
          <SheetTitle>{isEditing ? "Edit leave request" : "Request Leave"}</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {submitError ? (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="leave-request-submit-error"
            >
              {submitError}
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="leave-type">Leave Type</Label>
            <select
              id="leave-type"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.leave_type_id}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  leave_type_id: Number(event.target.value),
                }))
              }
            >
              {TEMP_LEAVE_TYPES.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-start-date">Start Date</Label>
            <Input
              id="leave-start-date"
              type="date"
              value={form.start_date}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, start_date: event.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-end-date">End Date</Label>
            <Input
              id="leave-end-date"
              type="date"
              value={form.end_date}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, end_date: event.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-booking-unit">Booking Unit</Label>
            <select
              id="leave-booking-unit"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.booking_unit}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  booking_unit: event.target.value as "full" | "half",
                }))
              }
            >
              <option value="full">Full day</option>
              <option value="half">Half day</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-reason">Comment</Label>
            <Textarea
              id="leave-reason"
              rows={4}
              value={form.reason}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, reason: event.target.value }))
              }
              placeholder="Add any additional notes..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="leave-attachment-url">Attachment URL (optional)</Label>
            <Input
              id="leave-attachment-url"
              type="url"
              value={form.attachment_url}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  attachment_url: event.target.value,
                }))
              }
              placeholder="https://"
            />
          </div>
        </div>

        <div className="mt-8 space-y-3">
          <Button className="w-full" disabled={isSubmitDisabled} onClick={handleSubmit}>
            {isEditing ? "Save changes" : "Submit request"}
          </Button>
          {isEditing && editingRequest?.status === "pending" ? (
            <Button
              className="w-full"
              variant="destructive"
              disabled={isPending}
              onClick={handleCancelRequest}
            >
              Cancel request
            </Button>
          ) : null}
          <Button className="w-full" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
